import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AiOutlineMail, AiOutlinePhone, AiFillFacebook, AiFillInstagram, AiFillTwitterCircle, AiFillLinkedin, AiFillYoutube } from "react-icons/ai";
import { gsap } from "gsap";

function Footer() {
  const footerRef = useRef(null);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) {
      return undefined;
    }

    gsap.set(footer, { opacity: 0, y: 60 });
    const columns = footer.querySelectorAll(".footer-column");
    gsap.set(columns, { opacity: 0, y: 30 });

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        return;
      }

      gsap.fromTo(
        footer,
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
        }
      );

      gsap.fromTo(
        columns,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power2.out",
        }
      );

      observer.disconnect();
    }, { rootMargin: "0px 0px -100px 0px" });

    observer.observe(footer);

    return () => {
      observer.disconnect();
      gsap.killTweensOf([footer, ...columns]);
    };
  }, []);

  const footerTitleClass =
    "text-lg font-semibold mb-4 tracking-wide bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent";

  const footerLinkClass =
    "relative text-sm text-gray-400 hover:text-cyan-300 transition-colors duration-300 " +
    "after:absolute after:left-0 after:-bottom-1 after:h-[1px] after:w-0 after:bg-cyan-400 " +
    "after:transition-all after:duration-300 hover:after:w-full";

  const socialIconClass =
    "p-2 rounded-full bg-white/5 hover:bg-cyan-500/20 border border-white/10 " +
    "hover:border-cyan-400 transition-all duration-300 transform hover:scale-110";

  const FooterLink = ({ to, children }) => (
    <Link to={to} className={footerLinkClass}>
      {children}
    </Link>
  );

  const FooterTitle = ({ children }) => (
    <h3 className={footerTitleClass}>{children}</h3>
  );


  return (
    <div className="relative bg-transparent">
      <footer ref={footerRef} className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-gray-300 pt-28 pb-12 px-6 md:px-16 lg:px-24 overflow-hidden">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="absolute top-0 left-0 w-full h-[90px] pointer-events-none"        >
          <path d="M0,80 C240,20 480,140 720,80 960,20 1200,140 1440,80 L1440,0 L0,0 Z" fill="#000000" />
        </svg>

        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute w-96 h-96 bg-indigo-400/20 rounded-full blur-3xl top-[4rem] left-[-4rem] animate-pulse-slow"></div>
          <div className="absolute w-80 h-80 bg-pink-400/20 rounded-full blur-3xl bottom-[-2rem] right-[-4rem] animate-pulse-slower"></div>
        </div>

        <div className="relative z-10 max-w-screen-xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="footer-column backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300">
            <Link to="/" className="inline-block mb-4">
              <img src="/logo.png" alt="Shree Ji Secure Soft Tech Logo" className="h-12 w-auto" />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed">
              A path towards infinity.
            </p>
            <div>
              <h3 className="text-lg font-semibold mb-4 tracking-wide bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Contact Us</h3>
              <a href="mailto:support@shreejisecuresofttech.com" className="flex items-start gap-2 text-sm hover:text-cyan-300 transition-colors duration-300 group">
                <AiOutlineMail size={18} className="mt-[2px] text-cyan-500 group-hover:text-cyan-300 transition-colors duration-300 shrink-0" />
                <span className="break-all"> support@shreejisecuresofttech.com </span>
              </a>
              <a href="mailto:support@shreejisecuresofttech.com" className="flex items-start gap-2 text-sm hover:text-cyan-300 transition-colors duration-300 group">
                <AiOutlinePhone size={18} className="mt-[2px] text-cyan-500 group-hover:text-cyan-300 transition-colors duration-300 shrink-0" />
                <span className="break-all"> +91 7009136956 </span>
              </a>
            </div>
          </div>

          <div className="footer-column">
            <FooterTitle>Quick Links</FooterTitle>
            <ul className="space-y-3">
              <li><FooterLink to="/about">About Us</FooterLink></li>
              <li><FooterLink to="/contact">Contact Us</FooterLink></li>
            </ul>
          </div>

          <div className="footer-column">
            <FooterTitle>Policies</FooterTitle>
            <ul className="space-y-3">
              <li><FooterLink to="/privacy-policy">Privacy Policy</FooterLink></li>
              <li><FooterLink to="/terms-conditions">Terms & Conditions</FooterLink></li>
              <li><FooterLink to="/shipping-policy">Shipping Policy</FooterLink></li>
              <li><FooterLink to="/refund-policy">Return, Refund and Cancellation Policy</FooterLink></li>
              <li><FooterLink to="/payment-policy">Payment Policy</FooterLink></li>
              <li><FooterLink to="/cookie-policy">Cookie Policy</FooterLink></li>
            </ul>
          </div>

          <div className="footer-column">
            <FooterTitle>Follow Us</FooterTitle>
            <div className="flex space-x-4">
              <a href="https://youtube.com/@theamritlok" target="_blank" rel="noopener noreferrer" className={socialIconClass}>
                <AiFillYoutube size={26} />
              </a>
              <a href="https://facebook.com/theamritlok" target="_blank" rel="noopener noreferrer" className={socialIconClass}>
                <AiFillFacebook size={26} />
              </a>
              <a href="https://instagram.com/theamritlok" target="_blank" rel="noopener noreferrer" className={socialIconClass}>
                <AiFillInstagram size={26} />
              </a>
              <a href="https://x.com/thelordkrishnag" target="_blank" rel="noopener noreferrer" className={socialIconClass}>
                <AiFillTwitterCircle size={26} />
              </a>
              <a href="https://linkedin.com/company/sjsst" target="_blank" rel="noopener noreferrer" className={socialIconClass}>
                <AiFillLinkedin size={26} />
              </a>
            </div>
          </div>

        </div>

        <div className="relative z-10 mt-16 pt-8 text-center">
          <div className="mx-auto mb-6 h-px w-2/3 bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
          <p className="text-gray-500 text-xs tracking-wide">
            © {new Date().getFullYear()} Shree Ji Secure Soft Tech. All Rights Reserved.
          </p>
        </div>

      </footer>
    </div>
  );
}

export default Footer;
